import React, { useState, useCallback } from 'react';
import { pick, prop } from 'ramda';
import { useTranslations } from '@core/js/hooks/i18n';
import { useAppSelector, useUserSelector } from '@core/js/store/hooks';

import { Control } from '@theme/js/components/common/Fields/Control';
import withField from '@core/js/components/common/Fields/withField';
import ValidationIcon from '@core/js/components/common/Fields/Icons/ValidationIcon';
import VisibilityIcon from '@theme/js/components/common/Fields/Icons/VisibilityIcon';
import VisibilityPasswordIcon from '@theme/js/components/common/Fields/Icons/VisibilityPasswordIcon';
import { InputShallow } from '@core/js/components/common/Fields/Input/components/InputShallow';

export const InputBare = ({
    /* rff render props */
    input,
    meta,

    /* user provided props */
    id,
    name,
    label,
    hint,
    readOnly,
    bottomHint,
    type = 'text',
    className = '',
    classNames,
    onClick,
    infoTooltip,
    currencySymbol,
    onMouseDown,
    placeholder = '',
    isLabelAsteriskEnabled,
    isPlaceholderAsteriskEnabled,
    isPasswordForm = false, /* TotalCasino changes */

    ...inputDomProps
}) => {
    const translate = useTranslations('playtech.mobile.system.input.');
    const { active, touched, error, validating, submitting } = meta;
    const isEnablePasswordSpy = useAppSelector(prop('isEnablePasswordSpy'));
    const showPasswordIcon = isEnablePasswordSpy && type === 'password';
    const [isVisible, setIsVisible] = useState(false);
    const toggleVisible = () => setIsVisible(!isVisible);
    const { userDetails } = useUserSelector(pick(['userDetails'])); // TotalCasino changes
    const dataCurrencyAttribute = currencySymbol ? { 'data-currency-symbol': currencySymbol } : {};
    const preventClick = useCallback(event => event.preventDefault(), []);

    return (
        <Control {...{
            id,
            meta,
            readOnly,
            className,
            classNames,
            onClick,
            label,
            hint,
            bottomHint,
            animate: true,
            infoTooltip,
            isLabelAsteriskEnabled,
            ...dataCurrencyAttribute
        }}>
            {isPasswordForm && showPasswordIcon && <VisibilityPasswordIcon/>} {/* TotalCasino changes */}
            <InputShallow
                {...input}
                {...inputDomProps}
                id={id}
                className={`${isPasswordForm && showPasswordIcon ? 'passwordVisibleIcon' : ''}`} /* TotalCasino changes */
                name={name}
                {...(name === 'fullname' && { value: `${userDetails?.firstName} ${userDetails?.lastName}` })} /* TotalCasino changes */
                readOnly={readOnly}
                tabIndex={readOnly ? -1 : 0}
                type={isVisible ? 'text' : type}
                aria-label={inputDomProps['aria-label'] || translate('playtech.mobile.system.input.aria-label')}
                {...submitting || validating ? { disabled: true } : {}}
                onMouseDown={readOnly ? preventClick : onMouseDown}
                placeholder={`${placeholder}${isPlaceholderAsteriskEnabled ? '*' : ''}`}
            />

            {touched && !active && !validating && !readOnly && (
                <ValidationIcon
                    animate={true}
                    isValid={!error}
                    aria-label={error}
                    tabIndex={error ? 0 : -1}
                    className={classNames?.validationIcon}
                />
            )}

            {showPasswordIcon && (
                <VisibilityIcon
                    onClick={toggleVisible}
                    isVisible={isVisible}
                    aria-label={isVisible
                        ? translate('password.aria-label.hide')
                        : translate('password.aria-label.show')
                    }
                />
            )}
        </Control>
    );
};

export const Input = withField(InputBare);
